import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Unlock, Clock, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Dividers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  locked: {
    icon: Lock,
    title: <>Leave your <span className="italic text-[#FF1493]">email.</span></>,
    body: "Enter your email to request the résumé. Once Tanvi approves, this button becomes your boarding pass.",
    cta: "Request",
  },
  pending: {
    icon: Clock,
    title: <>Request <span className="italic text-[#FF1493]">under review.</span></>,
    body: "Tanvi reviews every request personally. Come back and enter the same email once approved — the résumé unlocks automatically.",
    cta: "Check again",
  },
  denied: {
    icon: Lock,
    title: <>Access <span className="italic text-[#FF1493]">not granted.</span></>,
    body: "Enter your email to request the résumé. Once Tanvi approves, this button becomes your boarding pass.",
    cta: "Request",
  },
  approved: {
    icon: Unlock,
    title: <>Cleared. <span className="italic text-[#FF1493]">Welcome aboard.</span></>,
    body: "Your résumé link is open in a new tab.",
    cta: null,
  },
};

const LockedButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid="resume-locked-button"
    className="group inline-flex items-center gap-4 rounded-full border border-[#FF1493]/50 bg-[#FF1493]/10 backdrop-blur px-10 py-5 font-tech text-xs tracking-[0.3em] uppercase text-[#FF66B2] transition-all duration-300 hover:bg-[#FF1493] hover:text-black hover:scale-105"
  >
    <Lock size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:-rotate-12" />
    Résumé · Restricted Access
  </button>
);

const GateCard = ({ state, email, setEmail, busy, onSubmit }) => {
  const copy = COPY[state];
  const Icon = copy.icon;
  return (
    <div className="w-full max-w-lg rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-8 md:p-10" data-testid="resume-gate-card">
      <p className="font-tech text-[10px] tracking-[0.35em] uppercase text-[#FF66B2] flex items-center justify-center gap-2">
        <Icon size={13} strokeWidth={1.5} /> Security Checkpoint
      </p>
      <h3 className="font-display font-black text-3xl md:text-4xl mt-4">{copy.title}</h3>
      <p className="text-white/55 text-sm md:text-base leading-relaxed mt-3">{copy.body}</p>
      {copy.cta && (
        <form onSubmit={onSubmit} className="mt-7 flex flex-col sm:flex-row gap-3" data-testid="resume-request-form">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="recruiter@company.com"
            data-testid="resume-email-input"
            className="field-line flex-1 rounded-full border border-white/20 bg-black/40 px-6 py-4 text-base text-white placeholder:text-white/30"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="resume-request-button"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FF1493] text-black font-tech text-xs tracking-[0.25em] uppercase px-7 py-4 transition-transform duration-300 hover:scale-105 disabled:opacity-50"
          >
            {copy.cta} <ArrowUpRight size={14} strokeWidth={1.5} />
          </button>
        </form>
      )}
    </div>
  );
};

export const ResumeGate = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState("locked");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/resume/request`, { email });
      setState(data.status);
      if (data.status === "approved") {
        toast.success("Cleared — opening the résumé.");
        window.open(data.url, "_blank", "noopener,noreferrer");
      } else if (data.status === "pending") {
        toast("Request logged — awaiting approval from Tanvi.");
      }
    } catch (err) {
      console.error("Résumé request failed", err);
      toast.error("Couldn't file the request. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="resume" className="relative pt-0 pb-24 md:pb-32 -mt-10 md:-mt-20" data-testid="resume-gate-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col items-center text-center">
        <Reveal>
          {open ? (
            <GateCard state={state} email={email} setEmail={setEmail} busy={busy} onSubmit={submit} />
          ) : (
            <LockedButton onClick={() => setOpen(true)} />
          )}
        </Reveal>
      </div>
    </section>
  );
};
